import {
  Badge,
  Button,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { History as HistoryIcon, Save as SaveIcon } from "@mui/icons-material";
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";

import { SectionCard } from "~/src/components/common/SectionCard";
import { chakraFactory } from "~/src/lib/chakra-ui";

const StyledSectionCard = chakraFactory(SectionCard, {
  baseStyle: {
    maxWidth: "900px",
    margin: "24px auto 0 auto",
  },
});

const StyledEditor = chakraFactory(Textarea, {
  baseStyle: {
    fontFamily: "monospace",
    fontSize: "14px",
    minHeight: "300px",
  },
});

type ModuleItem = {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  hasDraft: boolean;
  tagContent: string | null;
  draftContent: string | null;
};

type Props = {
  module: ModuleItem;
  onSaveDraft: (moduleId: string, content: string) => Promise<void>;
  onDiscardDraft: (moduleId: string) => Promise<void>;
  onRollback?: (moduleId: string, versionId: string) => Promise<void>;
  onClose: () => void;
};

export const ModuleEditor: FC<Props> = ({
  module,
  onSaveDraft,
  onDiscardDraft,
  onRollback: _onRollback,
  onClose,
}) => {
  const [draftCode, setDraftCode] = useState(
    module.draftContent || module.tagContent || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setDraftCode(module.draftContent || module.tagContent || "");
  }, [module.draftContent, module.tagContent]);

  const handleSaveDraft = useCallback(async () => {
    if (!draftCode.trim()) {
      toast({
        title: "コードが空です",
        status: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      await onSaveDraft(module.id, draftCode);
      toast({
        title: "保存しました",
        status: "success",
      });
    } catch (error) {
      toast({
        title: "保存に失敗しました",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [draftCode, module.id, onSaveDraft, toast]);

  const handleDiscardDraft = useCallback(async () => {
    if (!module.hasDraft) {
      toast({
        title: "破棄する下書きがありません",
        status: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      await onDiscardDraft(module.id);
      setDraftCode(module.tagContent || "");
      toast({
        title: "下書きを破棄しました",
        status: "success",
      });
    } catch (error) {
      toast({
        title: "下書きの破棄に失敗しました",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [module.hasDraft, module.id, module.tagContent, onDiscardDraft, toast]);

  const hasUnsavedChanges =
    draftCode !== (module.draftContent || module.tagContent || "");

  const handleDraftCodeChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setDraftCode(e.target.value);
    },
    []
  );

  return (
    <StyledSectionCard>
      <VStack spacing="24px" align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing="4px">
            <HStack spacing="8px">
              <Text fontSize="lg" fontWeight="bold">
                {module.name}
              </Text>
              {module.hasDraft && (
                <Badge colorScheme="orange" size="sm">
                  下書きあり
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.600">
              優先度: {module.priority} | 状態:{" "}
              {module.enabled ? "有効" : "無効"}
            </Text>
          </VStack>
          <Button variant="ghost" onClick={onClose}>
            閉じる
          </Button>
        </HStack>

        {/* Tabs for Editor and History */}
        <Tabs>
          <TabList>
            <Tab>コードエディター</Tab>
            <Tab>
              <HStack spacing="4px">
                <HistoryIcon fontSize="small" />
                <Text>バージョン履歴</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Code Editor Tab */}
            <TabPanel p={0} pt="16px">
              <VStack spacing="16px" align="stretch">
                {/* Action Buttons */}
                <HStack spacing="12px">
                  <Button
                    leftIcon={<SaveIcon />}
                    colorScheme="blue"
                    size="sm"
                    onClick={handleSaveDraft}
                    isLoading={isLoading}
                    isDisabled={!hasUnsavedChanges}
                  >
                    保存
                  </Button>
                  {module.hasDraft && (
                    <Button
                      variant="ghost"
                      colorScheme="red"
                      size="sm"
                      onClick={handleDiscardDraft}
                      isLoading={isLoading}
                    >
                      下書き破棄
                    </Button>
                  )}
                </HStack>

                {/* Code Editor */}
                <StyledEditor
                  value={draftCode}
                  onChange={handleDraftCodeChange}
                  placeholder="JavaScriptコードを入力してください..."
                />

                {hasUnsavedChanges && (
                  <Text fontSize="xs" color="orange.500">
                    * 未保存の変更があります
                  </Text>
                )}
              </VStack>
            </TabPanel>

            {/* Version History Tab */}
            <TabPanel p={0} pt="16px">
              <VStack spacing="12px" align="stretch">
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  バージョン履歴
                </Text>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  textAlign="center"
                  py="24px"
                >
                  バージョン履歴がありません
                </Text>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </StyledSectionCard>
  );
};
